


import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios'; // adjust to your actual axios instance path

const SWIPE_THRESHOLD = 120;
const TAP_MOVE_THRESHOLD = 8; // px — below this it's a tap, not a drag
const TAP_TIME_THRESHOLD = 300; // ms — below this it's a tap, not a drag

// const BACKEND_URL = 'http://127.0.0.1:8000';

// Resolves a usable <img> src from either a full URL or a relative storage path
// (same helper as JobSearch.jsx, so logos resolve consistently everywhere)
const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

export default function DiscoveryModel({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [exiting, setExiting] = useState(null); // 'left' | 'right' | null — drives the fly-off animation
  const [lastSkipped, setLastSkipped] = useState(null); // { id, ...job } for Undo
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartTime = useRef(0);
  const pointerId = useRef(null);

  const startSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/discovery/start-session');
      sessionStorage.setItem('discovery_session_token', res.data.session_token);
      setSessionToken(res.data.session_token);
      return res.data.session_token;
    } catch (e) {
      setError('Could not start discovery session.');
      return null;
    }
  }, []);

  const fetchNext = useCallback(async (token) => {
    setLoading(true);
    setShowReportForm(false);
    try {
      const res = await api.get('/discovery/next-job', { params: { session_token: token } });
      if (!res.data.data) {
        const newToken = await startSession();
        if (newToken) {
          const res2 = await api.get('/discovery/next-job', { params: { session_token: newToken } });
          setJob(res2.data.data);
        } else {
          setJob(null);
        }
      } else {
        setJob(res.data.data);
      }
    } catch (e) {
      setError('Could not load the next job.');
    } finally {
      setLoading(false);
      setDragX(0);
      setDragY(0);
    }
  }, [startSession]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const existingToken = sessionStorage.getItem('discovery_session_token');
      if (existingToken) {
        setSessionToken(existingToken);
        await fetchNext(existingToken);
      } else {
        const token = await startSession();
        if (token) await fetchNext(token);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSkip = async () => {
    if (!job) return;
    const skippedJob = job;
    try {
      await api.post('/jobs/skip', { job_posting_id: job.id });
      setLastSkipped(skippedJob);
    } finally {
      fetchNext(sessionToken);
    }
  };

  const handleUndo = async () => {
    if (!lastSkipped) return;
    await api.post('/jobs/skip/undo', { job_posting_id: lastSkipped.id });
    setJob(lastSkipped);
    setLastSkipped(null);
  };

  const handleApply = async () => {
    if (!job) return;
    try {
      await api.post(`/jobs/${job.id}/apply`);
      setLastSkipped(null);
    } catch (e) {
      if (e.response?.status === 422 && e.response.data?.redirect === 'resume-upload') {
        navigate('/profile/resume');
        return;
      }
    } finally {
      fetchNext(sessionToken);
    }
  };

  const handleSave = async () => {
    if (!job) return;
    await api.post('/jobs/save', { job_posting_id: job.id });
    setJob((prev) => (prev ? { ...prev, saved: true } : prev));
  };

  const handleHideCompany = async () => {
    if (!job) return;
    try {
      await api.post('/jobs/hide-company', { employer_id: job.employer_id });
    } finally {
      fetchNext(sessionToken);
    }
  };

  const submitReport = async () => {
    if (!job || !reportReason) return;
    await api.post(`/jobs/${job.id}/report`, { reason: reportReason });
    setShowReportForm(false);
    setReportReason('');
  };

  const openDetails = () => {
    if (!job) return;
    onClose();
    navigate(`/jobs/${job.id}`);
  };

  // Animates the card fully off-screen, THEN performs the underlying
  // apply/skip action and swaps in the next job — this is what gives the
  // Tinder "card flies away" feel instead of an instant snap-to-next-card.
  const triggerSwipe = (direction) => {
    if (exiting) return; // ignore taps/releases mid-animation
    setIsDragging(false);
    setExiting(direction);
    setTimeout(() => {
      if (direction === 'right') handleApply();
      else handleSkip();
      setExiting(null);
      setDragX(0);
      setDragY(0);
    }, 300);
  };

  // --- Whole-card drag handling (mouse + touch via Pointer Events) ---

  const onCardPointerDown = (e) => {
    if (exiting || !job) return;
    pointerId.current = e.pointerId;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartY.current = e.clientY;
    dragStartTime.current = Date.now();
  };

  const onCardPointerMove = (e) => {
    if (!isDragging || exiting) return;
    setDragX(e.clientX - dragStartX.current);
    setDragY(e.clientY - dragStartY.current);
  };

  const onCardPointerUp = (e) => {
    if (!isDragging || exiting) return;
    setIsDragging(false);
    pointerId.current = null;

    const elapsed = Date.now() - dragStartTime.current;
    const movedFar = Math.abs(dragX) > TAP_MOVE_THRESHOLD || Math.abs(dragY) > TAP_MOVE_THRESHOLD;

    if (dragX > SWIPE_THRESHOLD) {
      triggerSwipe('right');
    } else if (dragX < -SWIPE_THRESHOLD) {
      triggerSwipe('left');
    } else if (!movedFar && elapsed < TAP_TIME_THRESHOLD) {
      // Tap → open job details
      setDragX(0);
      setDragY(0);
      openDetails();
    } else {
      // Released mid-drag without crossing the threshold → snap back
      setDragX(0);
      setDragY(0);
    }
  };

  // Interactive controls inside the card (buttons/select) shouldn't start a
  // drag or count as a card tap — stop the event before it reaches the card.
  const stopCardGesture = (e) => e.stopPropagation();

  // While flying off, translate fully off-screen with extra rotation and a
  // fade-out; while dragging/idle, follow the finger as before.
  const flyX = exiting === 'right' ? 600 : exiting === 'left' ? -600 : dragX;
  const flyY = exiting ? 0 : dragY * 0.15;
  const flyRotation = exiting === 'right' ? 25 : exiting === 'left' ? -25 : dragX / 20;
  const flyOpacity = exiting ? 0 : 1;
  const hintOpacity = Math.min(Math.abs(dragX) / SWIPE_THRESHOLD, 1);

  // Resolve the company logo the same way JobSearch does — checks the
  // direct field first, then falls back to a nested employer object.
  const logoSrc = job ? resolveLogoSrc(job.company_logo || job.employer?.company_logo) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px] px-4">
      <div className="relative w-full max-w-sm">

        {loading && !job && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden py-4 flex items-center justify-center text-slate-500">
            Finding your next match…
          </div>
        )}

        {!loading && !job && !error && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden py-4 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-slate-600">No more jobs to show right now.</p>
            <button onClick={() => startSession().then((t) => t && fetchNext(t))} className="px-6 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold shadow-sm hover:opacity-90 transition">Check again</button>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden py-4">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {job && (
          <>
          {/* Full swipeable card — the entire card (image + details) moves together. */}
          <div
            key={job.id}
            onPointerDown={onCardPointerDown}
            onPointerMove={onCardPointerMove}
            onPointerUp={onCardPointerUp}
            onPointerCancel={onCardPointerUp}
            style={{
              transform: `translate(${flyX}px, ${flyY}px) rotate(${flyRotation}deg)`,
              opacity: flyOpacity,
              transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s ease',
              willChange: 'transform, opacity',
              touchAction: 'none',
            }}
            className="relative bg-white rounded-3xl shadow-xl shadow-purple-500/20 border border-purple-100 overflow-hidden cursor-grab active:cursor-grabbing select-none"
          >
            {/* Image / banner area with badge overlays */}
            <div className="relative h-56 bg-gradient-to-br from-purple-600 to-pink-500 pointer-events-none">
              {logoSrc ? (
                <img src={logoSrc} alt={job.company_name || ''} className="w-full h-full object-cover" draggable={false} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                  {job.company_name?.[0] || '?'}
                </div>
              )}

              <div className="absolute top-3 left-3 flex items-center gap-1">
                {job.employer_verified && (
                  <span className="bg-emerald-500 text-white text-xs font-semibold px-2 py-1 rounded-full">✓ Verified</span>
                )}
              </div>
              {job.match_percentage != null && (
                <span className="absolute top-3 right-10 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
                  {Math.round(job.match_percentage)}% Match
                </span>
              )}
              <button
                onClick={onClose}
                onPointerDown={stopCardGesture}
                aria-label="Close"
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center text-sm pointer-events-auto transition"
              >
                ✕
              </button>

              <div
                className="absolute left-0 right-0 bottom-0 z-10 pointer-events-none px-3 py-2 border-2 border-red-500 text-red-500 font-bold rounded m-3 bg-white/80 w-fit rotate-[-12deg]"
                style={{ opacity: exiting === 'left' ? 1 : dragX < 0 ? hintOpacity : 0 }}
              >
                SKIP
              </div>
              <div
                className="absolute right-0 top-1/2 z-10 pointer-events-none px-3 py-2 border-2 border-green-500 text-green-500 font-bold rounded mr-3 bg-white/80 w-fit rotate-[12deg]"
                style={{ opacity: exiting === 'right' ? 1 : dragX > 0 ? hintOpacity : 0 }}
              >
                APPLY
              </div>

              {/* Big centered ✕ / ✓ stamp shown while the card is flying off,
                  so button taps (not just drags) get the same visual confirmation. */}
              {exiting === 'left' && (
                <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                  <div className="w-20 h-20 rounded-full bg-red-500/90 text-white flex items-center justify-center text-4xl font-bold shadow-lg">
                    ✕
                  </div>
                </div>
              )}
              {exiting === 'right' && (
                <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                  <div className="w-20 h-20 rounded-full bg-green-500/90 text-white flex items-center justify-center text-4xl font-bold shadow-lg">
                    ✓
                  </div>
                </div>
              )}

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3">
                <h2 className="text-white font-semibold text-lg leading-tight">{job.title}</h2>
                <p className="text-white/80 text-sm">{job.company_name}</p>
              </div>
            </div>

            {/* Details */}
            <div className="p-4">
              <div className="space-y-1.5 text-sm text-gray-600 mb-3 pointer-events-none">
                <p>💼 {job.job_type} · {job.work_mode}</p>
                <p>📍 {job.location || 'Not specified'}</p>
                <p>📈 {job.experience_required}+ yrs experience</p>
                {(job.salary_min || job.salary_max) && (
                  <p>💰 ₹{job.salary_min?.toLocaleString()} – ₹{job.salary_max?.toLocaleString()}</p>
                )}
              </div>

              {job.skills_required && (
                <div className="flex flex-wrap gap-1.5 mb-4 pointer-events-none">
                  {job.skills_required.split(',').slice(0, 5).map((s) => (
                    <span key={s} className="text-xs font-medium px-2.5 py-1 rounded-full bg-purple-50 text-purple-600">{s.trim()}</span>
                  ))}
                </div>
              )}

              {/* Action buttons — desktop-friendly controls that mirror the swipe gestures */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <button
                  onClick={handleUndo}
                  onPointerDown={stopCardGesture}
                  disabled={!lastSkipped}
                  aria-label="Undo"
                  className="w-11 h-11 rounded-full border border-amber-200 bg-white text-amber-500 flex items-center justify-center text-lg shadow-sm hover:bg-amber-50 transition disabled:opacity-30"
                >
                  ↺
                </button>
                <button
                  onClick={() => triggerSwipe('left')}
                  onPointerDown={stopCardGesture}
                  aria-label="Skip"
                  className="w-11 h-11 rounded-full border border-red-200 bg-white text-red-500 flex items-center justify-center text-lg shadow-sm hover:bg-red-50 transition"
                >
                  ✕
                </button>
                <button
                  onClick={handleSave}
                  onPointerDown={stopCardGesture}
                  aria-label="Save"
                  className={`w-11 h-11 rounded-full border flex items-center justify-center text-lg shadow-sm transition ${job.saved ? 'border-amber-300 bg-amber-50 text-amber-500' : 'border-gray-200 bg-white text-gray-400 hover:text-purple-600 hover:border-purple-300'}`}
                >
                  {job.saved ? '★' : '☆'}
                </button>
                <button
                  onClick={() => triggerSwipe('right')}
                  onPointerDown={stopCardGesture}
                  aria-label="Apply"
                  className="flex-1 h-11 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold shadow-md hover:opacity-90 transition"
                >
                  Apply
                </button>
              </div>

              {/* Secondary links */}
              <div className="flex items-center justify-center gap-4 text-xs font-semibold text-gray-500">
                <button onClick={openDetails} onPointerDown={stopCardGesture} className="hover:text-purple-600 hover:underline transition">View Full Details</button>
                <button onClick={handleHideCompany} onPointerDown={stopCardGesture} className="hover:text-purple-600 hover:underline transition">Hide Company</button>
                <button onClick={() => setShowReportForm((v) => !v)} onPointerDown={stopCardGesture} className="hover:text-purple-600 hover:underline transition">Report</button>
              </div>

              {showReportForm && (
                <div className="mt-3 p-3 rounded-xl border border-purple-100 bg-purple-50/40" onPointerDown={stopCardGesture}>
                  <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full border border-gray-200 rounded-xl p-2 text-sm mb-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400">
                    <option value="">Select a reason</option>
                    <option value="spam">Spam or misleading</option>
                    <option value="fraud">Looks fraudulent</option>
                    <option value="expired">Job already filled/expired</option>
                    <option value="other">Other</option>
                  </select>
                  <button onClick={submitReport} className="text-sm font-semibold px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm hover:opacity-90 transition">Submit report</button>
                </div>
              )}
            </div>
          </div>

          {/* Skip / Apply nav arrows — now sit BELOW the card (outside its
              rounded box) instead of pinned to the card's side edges. */}
          <div className="flex items-center justify-between px-2 mt-4">
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => triggerSwipe('left')}
                aria-label="Skip"
                className="w-11 h-11 rounded-full bg-white/95 border border-red-200 text-red-500 flex items-center justify-center text-xl shadow-md hover:bg-red-50"
              >
                ←
              </button>
              <span className="text-[11px] font-semibold text-red-500 bg-white/80 px-1.5 py-0.5 rounded-full">Skip</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => triggerSwipe('right')}
                aria-label="Apply"
                className="w-11 h-11 rounded-full bg-white/95 border border-green-200 text-green-500 flex items-center justify-center text-xl shadow-md hover:bg-green-50"
              >
                →
              </button>
              <span className="text-[11px] font-semibold text-green-500 bg-white/80 px-1.5 py-0.5 rounded-full">Apply</span>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}